import {
    getToken,
    setToken,
    getRefreshToken,
    setRefreshToken,
    getLastRefreshTime,
    setLastRefreshTime,
    shouldRefreshToken,
    removeToken,
    removeRefreshToken,
    removeLastRefreshTime,
} from "./auth";
import { refreshToken as refreshTokenApi, validateToken } from "../api/session";
import store from "../store";
import { getDomainSite } from "./site";

/**
 * Check if siteId is available
 * @returns {boolean}
 */
function hasSiteId() {
    // Check from store
    if (store.state.site && store.state.site.site && store.state.site.site._id) {
        return true;
    }
    // Check from localStorage
    const domainSite = getDomainSite();
    if (domainSite) {
        try {
            const site = JSON.parse(domainSite);
            if (site && site._id) {
                return true;
            }
        } catch (e) {
            // ignore
        }
    }
    return false;
}

/**
 * Token Refresh Manager
 * Handles automatic token refresh based on activity
 */
class TokenRefreshManager {
    constructor() {
        this.isRefreshing = false;
        this.refreshPromise = null;
        this.REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes in ms
    }

    /**
     * Check if enough time has passed since last refresh
     * @returns {boolean}
     */
    shouldRefresh() {
        const token = getToken();
        const refreshTokenValue = getRefreshToken();

        if (!token || !refreshTokenValue) {
            return false;
        }

        const lastRefresh = getLastRefreshTime();
        if (!lastRefresh) {
            return true; // Never refreshed, should refresh
        }

        const timeSinceLastRefresh = Date.now() - lastRefresh;
        return timeSinceLastRefresh >= this.REFRESH_INTERVAL;
    }

    /**
     * Refresh token if needed (called on API requests)
     * @returns {Promise<boolean>} True if refresh was successful or not needed
     */
    async refreshIfNeeded() {
        // Don't refresh if siteId is not available
        if (!hasSiteId()) {
            return true;
        }

        if (!this.shouldRefresh()) {
            return true;
        }

        return this.doRefresh();
    }

    /**
     * Perform token refresh
     * @returns {Promise<boolean>} True if refresh was successful
     */
    async doRefresh() {
        // Don't refresh if siteId is not available
        if (!hasSiteId()) {
            console.log("[TokenRefreshManager] Skipping refresh - siteId not available");
            return true;
        }

        // Prevent concurrent refresh requests
        if (this.isRefreshing) {
            return this.refreshPromise;
        }

        const refreshTokenValue = getRefreshToken();
        if (!refreshTokenValue) {
            return false;
        }

        this.isRefreshing = true;
        this.refreshPromise = this._performRefresh(refreshTokenValue);

        try {
            const result = await this.refreshPromise;
            return result;
        } finally {
            this.isRefreshing = false;
            this.refreshPromise = null;
        }
    }

    /**
     * Internal refresh implementation
     * @private
     */
    async _performRefresh(refreshTokenValue) {
        try {
            const response = await refreshTokenApi(refreshTokenValue);

            if (response.data && response.data.success) {
                // Update tokens in localStorage
                setToken(response.data.token);
                setRefreshToken(response.data.refreshToken);
                setLastRefreshTime(Date.now());

                // Also update Vuex store
                store.commit("auth/SET_TOKEN", response.data.token);

                console.log("[TokenRefreshManager] Token refreshed successfully");
                return true;
            }

            return false;
        } catch (error) {
            console.error("[TokenRefreshManager] Token refresh failed:", error.message);

            // If refresh fails with 401, clear tokens
            if (error.response && error.response.status === 401) {
                this.clearTokens();
            }

            return false;
        }
    }

    /**
     * Validate current token
     * @returns {Promise<Object>} Validation result
     */
    async validateCurrentToken() {
        // Don't validate if siteId is not available
        if (!hasSiteId()) {
            console.log("[TokenRefreshManager] Skipping validation - siteId not available");
            return { valid: true, reason: "site_not_loaded" };
        }

        try {
            const response = await validateToken();
            return response.data;
        } catch (error) {
            console.error("[TokenRefreshManager] Token validation failed:", error.message);
            return { valid: false, reason: "validation_error" };
        }
    }

    /**
     * Initialize on app startup
     * Checks if token exists and tries to refresh if needed
     * @returns {Promise<boolean>} True if token is valid
     */
    async initializeOnStartup() {
        const token = getToken();
        const refreshTokenValue = getRefreshToken();

        if (!token) {
            return false;
        }

        // Skip if siteId is not available yet
        if (!hasSiteId()) {
            console.log("[TokenRefreshManager] Skipping startup - siteId not available yet");
            return true; // Assume valid, refresh will happen in request interceptor
        }

        // If we have a refresh token and enough time has passed, try to refresh
        if (refreshTokenValue && this.shouldRefresh()) {
            console.log("[TokenRefreshManager] Startup refresh triggered");
            await this.doRefresh();
        }

        return true;
    }

    /**
     * Clear all tokens
     */
    clearTokens() {
        removeToken();
        removeRefreshToken();
        removeLastRefreshTime();
    }
}

// Export singleton instance
export const tokenRefreshManager = new TokenRefreshManager();

// Export for use in request interceptors
export async function refreshTokenIfNeeded() {
    return tokenRefreshManager.refreshIfNeeded();
}
